// `subscribeTimeline` remains authoritative; pagination generation marks
// completion separately so callers never consume the previous outcome.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  paginateBackward as matrixPaginateBackward,
  subscribeTimeline,
  type Client,
  type PaginateBackwardResult,
  type TimelineEvent,
} from "~/lib/matrix";

const PAGINATION_DELIVERY_TIMEOUT_MS = 5000;

/** Outcome of the most recently settled pagination request. */
export interface PaginationOutcome {
  /** Number of new events prepended by the most recent settled request. */
  loaded: number;
  /** True once the server reported no more history. */
  atStart: boolean;
  /** True when the request threw instead of resolving. */
  failed: boolean;
}

export interface UseTimelineResult {
  events: TimelineEvent[];
  isLoading: boolean;
  atStart: boolean;
  /**
   * Monotonic counter that increments once per fully-settled
   * pagination request (success, terminal empty page, `at-start`, or a
   * failure). Captured into a reading-anchor at fire time so the anchor
   * can recognise its own completion. Never resets within a room.
   */
  paginationGeneration: number;
  /** Outcome of the most recent settled pagination, or null until one settles. */
  lastPagination: PaginationOutcome | null;
  paginateBackward: (limit?: number) => Promise<PaginateBackwardResult | null>;
}

/**
 * Subscribe to a room's timeline. When `client` or `roomId` changes the
 * previous subscription is torn down and the new one mounted. Passing
 * `null` for either detaches — useful while the shell renders the
 * "no active room" empty state.
 */
export function useTimeline(
  client: Client | null,
  roomId: string | null,
  defaultPageSize = 50,
): UseTimelineResult {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [atStart, setAtStart] = useState(false);
  const [paginationGeneration, setPaginationGeneration] = useState(0);
  const [lastPagination, setLastPagination] = useState<PaginationOutcome | null>(null);
  // Guards against a stale pagination response being applied after the
  // user switches rooms.
  const subscriptionIdRef = useRef(0);
  const paginatingRef = useRef(false);
  const latestEventsRef = useRef<TimelineEvent[]>([]);
  const pendingSettleRef = useRef<{
    subId: number;
    oldestIdAtFire: string | null;
    outcome: PaginationOutcome;
  } | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settledRef = useRef(false);

  const clearPendingSettle = useCallback(() => {
    pendingSettleRef.current = null;
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const settle = useCallback(
    (subId: number, outcome: PaginationOutcome) => {
      if (settledRef.current) return;
      if (subscriptionIdRef.current !== subId) return;
      settledRef.current = true;
      clearPendingSettle();
      paginatingRef.current = false;
      setIsLoading(false);
      setPaginationGeneration((n) => n + 1);
      setLastPagination(outcome);
      if (outcome.atStart) setAtStart(true);
    },
    [clearPendingSettle],
  );

  useEffect(() => {
    if (!client || !roomId) {
      subscriptionIdRef.current += 1;
      setEvents([]);
      setAtStart(false);
      setIsLoading(false);
      paginatingRef.current = false;
      setLastPagination(null);
      clearPendingSettle();
      settledRef.current = true;
      return;
    }
    subscriptionIdRef.current += 1;
    const mySubId = subscriptionIdRef.current;
    settledRef.current = false;
    clearPendingSettle();
    setEvents([]);
    latestEventsRef.current = [];
    setAtStart(false);
    setIsLoading(false);
    paginatingRef.current = false;
    setLastPagination(null);
    const unsub = subscribeTimeline(
      client,
      roomId,
      (next) => {
        if (subscriptionIdRef.current !== mySubId) return;
        latestEventsRef.current = next;
        setEvents(next);
        const pending = pendingSettleRef.current;
        if (
          pending &&
          pending.subId === mySubId &&
          (next[0]?.id ?? null) !== pending.oldestIdAtFire
        ) {
          settle(mySubId, pending.outcome);
        }
      },
      { cache: true },
    );
    return () => {
      unsub();
      clearPendingSettle();
      settledRef.current = true;
    };
  }, [client, roomId, clearPendingSettle, settle]);

  const paginate = useCallback(
    async (limit: number = defaultPageSize): Promise<PaginateBackwardResult | null> => {
      if (!client || !roomId) return null;
      if (paginatingRef.current) return null;
      if (atStart) return null;
      paginatingRef.current = true;
      settledRef.current = false;
      clearPendingSettle();
      setIsLoading(true);
      const mySubId = subscriptionIdRef.current;
      const oldestIdAtFire = latestEventsRef.current[0]?.id ?? null;
      let result: PaginateBackwardResult | null = null;
      try {
        result = await matrixPaginateBackward(client, roomId, limit);
      } catch (err) {
        console.error("paginateBackward failed", err);
      }
      if (subscriptionIdRef.current !== mySubId) return null;
      const outcome: PaginationOutcome = {
        loaded: result?.loaded ?? 0,
        atStart: result?.atStart ?? false,
        failed: result === null,
      };
      const alreadyDelivered = (latestEventsRef.current[0]?.id ?? null) !== oldestIdAtFire;
      if (outcome.failed || outcome.loaded === 0 || alreadyDelivered) {
        settle(mySubId, outcome);
      } else {
        pendingSettleRef.current = { subId: mySubId, oldestIdAtFire, outcome };
        settleTimerRef.current = setTimeout(() => {
          if (pendingSettleRef.current?.subId !== mySubId) return;
          console.warn("pagination settled without delivery", roomId, outcome.loaded);
          settle(mySubId, outcome);
        }, PAGINATION_DELIVERY_TIMEOUT_MS);
      }
      return result;
    },
    [client, roomId, defaultPageSize, atStart, clearPendingSettle, settle],
  );

  return {
    events,
    isLoading,
    atStart,
    paginationGeneration,
    lastPagination,
    paginateBackward: paginate,
  };
}
